import { ComboVariationPricingFields } from '@/components/Deals/ComboVariationPricingFields'
import type { ComboVariation } from '@/types'
import { Plus, Trash2 } from 'lucide-react'
import { formatCurrency } from '@/utils/format-currency'
import { Button } from '@/components/ui/Button'
import { Input, Textarea } from '@/components/ui/Input'
import { getActiveComboVariationIndex } from './computeActivePlanContext'
import type { useProposalStudio } from './useProposalStudio'

type Studio = ReturnType<typeof useProposalStudio>

export function ProposalStudioComboEditors({ s }: { s: Studio }) {
  const variationIdx = getActiveComboVariationIndex(s.variationsWithPricing, s.activeIndex)
  const activeVar = s.comboVariations[variationIdx]
  const priced = s.variationsWithPricing[variationIdx]

  if (!activeVar || !priced) {
    return (
      <div className="text-xs text-text-muted p-4 border border-white/[0.06] rounded-lg">
        Nenhuma variação de combo configurada.
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between items-center pb-2 border-b border-[var(--glass-border)]">
        <h3 className="text-sm font-bold text-[var(--text-primary)] uppercase tracking-wider">
          Combo: {priced.nome}
        </h3>
        {s.comboVariations.length > 1 && (
          <span className="text-[10px] text-text-muted">
            Opção {variationIdx + 1} de {s.comboVariations.length}
          </span>
        )}
      </div>

      <Input
        label="Nome da Opção"
        value={activeVar.nome}
        onChange={(e) => s.handleUpdateActiveVariationField('nome', e.target.value)}
        placeholder="Ex: Combo 1"
      />

      <Textarea
        label="Descrição da Opção"
        rows={3}
        value={activeVar.descricao || ''}
        onChange={(e) => s.handleUpdateActiveVariationField('descricao', e.target.value)}
        placeholder="Ex: Pacote com desconto para contratação conjunta..."
      />

      <div className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-xl p-4 space-y-4">
        <h4 className="text-xs font-bold text-accent uppercase tracking-wider">
          Precificação & Descontos
        </h4>

        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 text-[10px] text-text-label uppercase tracking-wider font-semibold">
            Tipo de Desconto
            <select
              value={activeVar.precoFechado !== undefined ? 'precoFechado' : 'descontoPercentual'}
              onChange={(e) => {
                const type = e.target.value
                if (type === 'precoFechado') {
                  s.setComboVariations((prev) =>
                    prev.map((v, i) =>
                      i !== variationIdx
                        ? v
                        : { ...v, precoFechado: priced.valorOriginal, descontoPercentual: undefined },
                    ),
                  )
                } else {
                  s.setComboVariations((prev) =>
                    prev.map((v, i) =>
                      i !== variationIdx
                        ? v
                        : { ...v, descontoPercentual: 0, precoFechado: undefined },
                    ),
                  )
                }
              }}
              className="w-full bg-[var(--glass-bg)] border border-[var(--glass-border)] text-[var(--text-primary)] rounded px-2 py-1 outline-none text-xs font-semibold cursor-pointer"
            >
              <option value="descontoPercentual">% Percentual</option>
              <option value="precoFechado">Preço Fechado</option>
            </select>
          </label>

          {activeVar.precoFechado !== undefined ? (
            <Input
              label="Preço Fechado mensal (R$)"
              type="number"
              min={0}
              value={activeVar.precoFechado || 0}
              onChange={(e) => s.handleUpdateActiveVariationField('precoFechado', Number(e.target.value))}
            />
          ) : (
            <Input
              label="Desconto (%)"
              type="number"
              min={0}
              max={100}
              value={activeVar.descontoPercentual || 0}
              onChange={(e) => s.handleUpdateActiveVariationField('descontoPercentual', Number(e.target.value))}
            />
          )}
        </div>

        <div className="flex justify-between items-center pt-2 border-t border-[var(--glass-border)] text-xs">
          <span className="text-text-muted">De (mensal):</span>
          <span className="text-[#ff0000] font-semibold line-through">
            {formatCurrency(priced.valorOriginal)}/mês
          </span>
        </div>

        <div className="flex justify-between items-center text-xs">
          <span className="text-text-muted">Por (mensal):</span>
          <span className="text-[#40C351] font-black text-sm">
            {formatCurrency(priced.valorFinal)}/mês
          </span>
        </div>
      </div>

      <ComboVariationPricingFields
        variation={activeVar}
        priced={priced}
        onChange={(patch) => {
          s.setComboVariations((prev) =>
            prev.map((v, i) => (i !== variationIdx ? v : { ...v, ...patch })),
          )
        }}
      />

      <div className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-xl p-4 space-y-3">
        <h4 className="text-xs font-bold text-accent uppercase tracking-wider">
          Itens e Escopos Inclusos ({activeVar.itemIds.length})
        </h4>

        <div className="space-y-2 max-h-[220px] overflow-y-auto premium-scroll pr-1">
          {s.proposalItems.map((item) => {
            const isIncluded = activeVar.itemIds.includes(item.id)
            return (
              <label
                key={item.id}
                className={`flex items-center gap-3 text-xs p-2.5 rounded-lg border transition-all cursor-pointer ${
                  isIncluded
                    ? 'bg-[#40C351]/10 border-[#40C351]/30 text-[var(--text-primary)]'
                    : 'bg-[var(--glass-bg)] border-[var(--glass-border)] text-text-muted hover:bg-white/[0.04]'
                }`}
              >
                <input
                  type="checkbox"
                  checked={isIncluded}
                  onChange={(e) => {
                    const checked = e.target.checked
                    const newItemIds = checked
                      ? [...activeVar.itemIds, item.id]
                      : activeVar.itemIds.filter((id) => id !== item.id)
                    s.handleUpdateActiveVariationField('itemIds', newItemIds)
                  }}
                  className="rounded border-[var(--glass-border)] text-accent focus:ring-accent"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate text-[var(--text-primary)]">{item.descricao}</div>
                  <div className="text-[10px] text-text-muted">
                    {formatCurrency(item.pricing?.installmentValue ?? item.valorUnitario)}/mês
                  </div>
                </div>
              </label>
            )
          })}
        </div>
      </div>

      {s.comboVariations.length > 1 && (
        <Button
          variant="secondary"
          size="sm"
          className="w-full text-xs"
          icon={<Plus size={13} />}
          onClick={() => {
            const newId = `v-${Date.now()}`
            const newVar: ComboVariation = {
              id: newId,
              nome: `Opção ${s.comboVariations.length + 1}`,
              descricao: 'Customização de escopo e valores para o cliente.',
              itemIds: s.proposalItems.map((item) => item.id),
              descontoPercentual: 0,
            }
            s.setComboVariations((prev) => [...prev, newVar])
          }}
        >
          Adicionar Nova Opção de Combo
        </Button>
      )}

      {s.comboVariations.length > 1 && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-xs text-red-400"
          icon={<Trash2 size={13} />}
          onClick={() => {
            s.setComboVariations((prev) => prev.filter((v) => v.id !== activeVar.id))
            s.setActiveIndex(0)
          }}
        >
          Remover esta opção
        </Button>
      )}
    </div>
  )
}
